import { useState, useEffect, useCallback } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useCartStore } from "@/stores/cartStore";
import { useNavigate } from "react-router-dom";
import rudolphPeek from "@/assets/rudolph-peek.png";

const EXIT_POPUP_SHOWN_KEY = "exit_popup_shown";
const EMAIL_SUBSCRIBED_KEY = "email_subscribed";

export const ExitIntentPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const cartItems = useCartStore((state) => state.items);
  const navigate = useNavigate();

  const shouldShowPopup = useCallback(() => {
    // Don't show if already shown this session
    if (sessionStorage.getItem(EXIT_POPUP_SHOWN_KEY)) return false;
    // Don't show if already subscribed
    if (localStorage.getItem(EMAIL_SUBSCRIBED_KEY)) return false;
    // Don't show if user has items in cart (they're engaged)
    if (cartItems.length > 0) return false;
    return true;
  }, [cartItems.length]);

  const showPopup = useCallback(() => {
    if (shouldShowPopup()) {
      setIsOpen(true);
      sessionStorage.setItem(EXIT_POPUP_SHOWN_KEY, "true");
    }
  }, [shouldShowPopup]);

  useEffect(() => {
    // Desktop: Mouse exit intent (leaving viewport from top)
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) {
        showPopup();
      }
    };

    // Mobile: Scroll up gesture after delay
    let mobileTimeout: NodeJS.Timeout;
    let lastScrollY = window.scrollY;
    let scrollUpCount = 0;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY < lastScrollY && currentScrollY > 200) {
        scrollUpCount++;
        if (scrollUpCount >= 3) {
          showPopup();
          scrollUpCount = 0;
        }
      } else {
        scrollUpCount = 0;
      }
      lastScrollY = currentScrollY;
    };

    // Start tracking after 30 seconds on mobile
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      mobileTimeout = setTimeout(() => {
        window.addEventListener("scroll", handleScroll, { passive: true });
      }, 30000);
    } else {
      document.addEventListener("mouseleave", handleMouseLeave);
    }

    return () => {
      document.removeEventListener("mouseleave", handleMouseLeave);
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(mobileTimeout);
    };
  }, [showPopup]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from("email_subscribers")
        .insert({ email: email.trim(), source: "exit_popup" });

      if (error) {
        if (error.code === "23505") {
          // Duplicate email
          toast.success("You're already on our list!");
        } else {
          throw error;
        }
      } else {
        toast.success("Thanks! We'll keep you posted.");
        localStorage.setItem(EMAIL_SUBSCRIBED_KEY, "true");
      }
      setIsOpen(false);
      setEmail("");
    } catch (error) {
      console.error("Error subscribing:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md p-0 gap-0 border-border overflow-visible">
        {/* Rudolph peeking over the top */}
        <img 
          src={rudolphPeek} 
          alt="Rudolph peeking"
          className="absolute -top-[110px] left-1/2 -translate-x-1/2 w-20 md:w-24 z-20"
          style={{ animationDelay: '0s', opacity: 1 }}
        />
        
        <button
          onClick={() => setIsOpen(false)}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 z-10"
        >
          <X className="h-5 w-5 text-foreground" />
          <span className="sr-only">Close</span>
        </button>

        <div className="p-8 pt-6 text-center rounded-lg bg-background">
          <h2 className="font-cormorant text-3xl md:text-4xl font-bold text-foreground mb-4">
            Wait — Your Tree Deserves Better
          </h2>
          
          <p className="text-muted-foreground font-inter mb-3">
            Most Christmas trees start dropping needles within 2 weeks.{" "}
            <span className="text-foreground font-semibold">Yours doesn't have to.</span>
          </p>
          
          <p className="text-foreground font-inter font-medium mb-6">
            One bottle helps keep your tree fresh, green, and fragrant all season — with that Christmas morning scent.
          </p>

          <Button
            onClick={() => {
              navigate('/products');
              setIsOpen(false);
            }}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-inter font-semibold h-12 text-base mb-4"
          >
            Keep My Tree Fresh
          </Button>

          <p className="text-sm text-muted-foreground font-inter mb-3">
            Or get notified about deals:
          </p>

          <form onSubmit={handleSubmit} className="space-y-3">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-12 text-base font-inter"
            />
            <Button
              type="submit"
              variant="outline"
              disabled={loading}
              className="w-full font-inter font-semibold h-12 text-base"
            >
              {loading ? "Subscribing..." : "Keep Me Posted"}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};